import React, { useState, useRef } from "react";
import {
  Plus,
  Trash2,
  LayoutTemplate,
  Upload,
  Image as ImageIcon,
  Loader2,
  Maximize2,
  RefreshCw,
  Clipboard,
  X,
  Library,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { uploadFile } from "@/lib/uploadFile";
import { getUploadUrl, validateUploadFile, MAX_ATTACHMENT_SIZE, extractErrorMessage } from "@/lib/utils";
import { SelectFromLayoutLibraryDialog } from "./SelectFromLayoutLibraryDialog";

interface LayoutCardProps {
  layout: any;
  index: number;
  onUpdate: (updates: any) => void;
  onRemove: () => void;
  onSelectFromLibrary: () => void;
}

function LayoutItemCard({
  layout,
  index,
  onUpdate,
  onRemove,
  onSelectFromLibrary,
}: LayoutCardProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processUpload = async (file: File) => {
    try {
      validateUploadFile(file, {
        maxSize: MAX_ATTACHMENT_SIZE,
        label: "Ảnh bài bình",
      });
      setIsUploading(true);
      const res = await uploadFile(file);
      onUpdate({ fileUrl: res.url });
      toast.success("Đã tải ảnh bài bình lên thành công");
    } catch (err: any) {
      toast.error(extractErrorMessage(err, "Tải ảnh thất bại"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processUpload(file);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await processUpload(file);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          await processUpload(file);
          break;
        }
      }
    }
  };

  const imageUrl = layout.fileUrl ? getUploadUrl(layout.fileUrl) : null;

  return (
    <div
      tabIndex={0}
      onPaste={handlePaste}
      className="flex flex-col gap-3 p-4 border border-slate-200 rounded-lg bg-white shadow-sm hover:border-slate-300 transition-colors focus:outline-none focus:ring-1 focus:ring-indigo-400"
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="h-5 px-1.5 text-xs font-mono font-bold bg-indigo-50 text-indigo-700 border-indigo-200">
            #{index + 1}
          </Badge>
          <span className="text-xs font-bold text-slate-800">
            {layout.name || `Bài bình ${index + 1}`}
          </span>
          {layout.layoutCode && (
            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 font-mono text-slate-500">
              {layout.layoutCode}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            onClick={onSelectFromLibrary}
          >
            <Library className="h-3.5 w-3.5 mr-1" /> Thư Viện Mẫu
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
            title="Xóa bài bình"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main 2-Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-stretch">
        {/* Left Column (Dropzone & Image Preview - 4 cols) */}
        <div className="md:col-span-4 flex flex-col justify-between">
          <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center justify-between">
            <span className="flex items-center gap-1">
              <ImageIcon className="h-3.5 w-3.5 text-indigo-600" /> Ảnh Bài Bình (Prepress Marquette)
            </span>
            {imageUrl && (
              <span className="text-[10px] text-emerald-600 font-medium">Đã có ảnh</span>
            )}
          </label>

          <div
            className={`relative flex-1 min-h-[160px] rounded-md border-2 border-dashed transition-all flex flex-col items-center justify-center p-3 overflow-hidden ${
              isDragging
                ? "border-indigo-500 bg-indigo-50/50"
                : imageUrl
                ? "border-slate-200 bg-slate-50/50"
                : "border-slate-200 bg-slate-50 hover:bg-slate-100/70"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <div className="flex flex-col items-center gap-1.5 text-indigo-600">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-xs font-medium">Đang tải ảnh...</span>
              </div>
            ) : imageUrl ? (
              <div className="relative w-full h-full flex items-center justify-center group">
                <img
                  src={imageUrl}
                  alt="Bài bình"
                  className="max-h-[140px] w-auto object-contain rounded drop-shadow-sm transition-transform group-hover:scale-105 cursor-pointer"
                  onClick={() => setShowLightbox(true)}
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 rounded">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 px-2 text-xs bg-white text-slate-800 shadow"
                    onClick={() => setShowLightbox(true)}
                  >
                    <Maximize2 className="h-3 w-3 mr-1" /> Xem lớn
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 px-2 text-xs bg-white text-slate-800 shadow"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Đổi
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="h-7 px-2 text-xs shadow"
                    onClick={() => onUpdate({ fileUrl: null })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-1.5 select-none">
                <div className="mx-auto w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Upload className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-700">
                    Kéo thả ảnh hoặc{" "}
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline font-bold"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      chọn file
                    </button>
                  </p>
                  <p className="text-[10px] text-slate-400">
                    PNG, JPG, WebP, SVG
                  </p>
                </div>
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50/70 text-[9px] text-indigo-700 font-medium">
                  <Clipboard className="h-2.5 w-2.5" /> Hỗ trợ dán (Ctrl+V)
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Right Column (Technical Parameters Grid - 8 cols) */}
        <div className="md:col-span-8 flex flex-col justify-between space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Tên Bài Bình
              </label>
              <Input
                value={layout.name || ""}
                onChange={(e) => onUpdate({ name: e.target.value })}
                placeholder="VD: In Thân Hộp, In Nắp..."
                className="h-8 text-xs bg-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Khổ Giấy In (cm)
              </label>
              <Input
                value={layout.sheetSize || ""}
                onChange={(e) => onUpdate({ sheetSize: e.target.value })}
                placeholder="VD: 65x86"
                className="h-8 text-xs bg-white font-mono"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Số Con / Tờ (Ups)
              </label>
              <Input
                type="number"
                value={layout.ups || ""}
                onChange={(e) => onUpdate({ ups: Number(e.target.value) })}
                className="h-8 text-xs bg-white font-mono font-bold"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Kiểu Bình Bài
              </label>
              <Input
                value={layout.prepressType || ""}
                onChange={(e) => onUpdate({ prepressType: e.target.value })}
                placeholder="Tự trở, Trở nhíp..."
                className="h-8 text-xs bg-white"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Cạnh Nhíp (mm)
              </label>
              <Input
                type="number"
                value={layout.gripperMargin || ""}
                onChange={(e) => onUpdate({ gripperMargin: Number(e.target.value) })}
                placeholder="12"
                className="h-8 text-xs bg-white font-mono"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Bù Lề / Xén (mm)
              </label>
              <Input
                type="number"
                value={layout.bleed || ""}
                onChange={(e) => onUpdate({ bleed: Number(e.target.value) })}
                placeholder="2"
                className="h-8 text-xs bg-white font-mono"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Số Kẽm Offset
              </label>
              <Input
                type="number"
                value={layout.plateCount || ""}
                onChange={(e) => onUpdate({ plateCount: Number(e.target.value) })}
                placeholder="4"
                className="h-8 text-xs bg-white font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1 border-t border-slate-100">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Định Lượng Giấy (GSM)
              </label>
              <Input
                type="number"
                value={layout.gsm || ""}
                onChange={(e) => onUpdate({ gsm: Number(e.target.value) })}
                placeholder="VD: 350"
                className="h-8 text-xs bg-white font-mono"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                Tổng Số Tờ Cần Chạy
              </label>
              <Input
                type="number"
                value={layout.totalSheetsRequired || ""}
                onChange={(e) => onUpdate({ totalSheetsRequired: Number(e.target.value) })}
                placeholder="0"
                className="h-8 text-xs bg-white font-mono font-bold text-indigo-700"
              />
            </div>
          </div>
          {/* Ghi chú bình bài (thay ô "Ghi chú bình bài prepress" của khối thông số gia công cũ) */}
          <div className="space-y-1 mt-3">
            <label className="text-xs font-semibold text-slate-600">Ghi Chú Bình Bài</label>
            <Input
              data-testid="print-layout-notes"
              value={layout.notes || ""}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder="VD: bình 2 mặt tay in, chừa nhíp 10mm..."
              className="h-8 text-xs bg-white"
            />
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {imageUrl && (
        <Dialog open={showLightbox} onOpenChange={setShowLightbox}>
          <DialogContent className="max-w-4xl p-2 bg-slate-950 border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{layout.name || "Bản Bình Marquette"} ({layout.sheetSize || "—"} cm, {layout.ups || 1} con)</span>
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-4 max-h-[80vh] overflow-auto">
              <img
                src={imageUrl}
                alt={layout.name}
                className="max-h-[75vh] w-auto object-contain rounded drop-shadow-xl"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export function PrintLayoutBuilder({ form }: { form: any }) {
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const [selectedLayoutIndex, setSelectedLayoutIndex] = useState<number | null>(null);

  return (
    <form.Field
      name="printLayouts"
      mode="array"
      children={(field: any) => {
        const printLayouts = field.state.value || [];

        const handleAdd = () => {
          field.pushValue({
            id: crypto.randomUUID(),
            name: `Bài bình ${printLayouts.length + 1}`,
            totalSheetsRequired: 0,
            gsm: null,
            prepressType: "Tự trở",
            sheetSize: "65x86",
            ups: 1,
            gripperMargin: 12,
            bleed: 2,
            plateCount: 4,
            fileUrl: null,
            layoutId: null,
          });
        };

        const updatePrintLayout = (index: number, updates: any) => {
          const current = printLayouts[index];
          field.setValueAt(index, { ...current, ...updates });
        };

        const removePrintLayout = (index: number) => {
          field.removeValue(index);
        };

        const handleSelectFromLibrary = (layout: any) => {
          if (selectedLayoutIndex !== null && selectedLayoutIndex >= 0) {
            updatePrintLayout(selectedLayoutIndex, layout);
          } else {
            field.pushValue({
              id: crypto.randomUUID(),
              ...layout,
              totalSheetsRequired: 0,
              prepressType: "Tự trở",
              bleed: 2,
              plateCount: 4,
            });
          }
          setSelectedLayoutIndex(null);
        };

        return (
          <Card className="border-none shadow-sm ring-1 ring-slate-200">
            <CardHeader className="bg-slate-50/60 py-3 border-b flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-800">
                  <LayoutTemplate className="h-4 w-4 text-indigo-600" />
                  Kế Hoạch Bản Bình Chế Bản (Prepress Layouts &amp; Marquette)
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Quản lý các bài in độc lập trong lệnh (Nắp, thân, khay...), upload hình ảnh bài bình và khổ in tương ứng
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedLayoutIndex(-1);
                    setLibraryModalOpen(true);
                  }}
                  className="h-8 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  <Library className="h-3.5 w-3.5 mr-1.5" /> Thêm Từ Thư Viện
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAdd}
                  className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Thêm Bài Bình Mới
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {printLayouts.length === 0 ? (
                <div className="text-center py-8 px-4 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 space-y-3">
                  <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                    <LayoutTemplate className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-700">
                      Chưa có bản bình nào trong lệnh sản xuất này
                    </p>
                    <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                      Thêm bài bình để gắn hình ảnh marquette, khổ giấy in, số con (ups) và tính toán định mức kẽm CTP
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-2 pt-1">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAdd}
                      className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Thêm Bài Bình
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {printLayouts.map((layout: any, index: number) => (
                    <LayoutItemCard
                      key={layout.id || index}
                      layout={layout}
                      index={index}
                      onUpdate={(updates) => updatePrintLayout(index, updates)}
                      onRemove={() => removePrintLayout(index)}
                      onSelectFromLibrary={() => {
                        setSelectedLayoutIndex(index);
                        setLibraryModalOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>

            {/* Layout Library Selection Modal */}
            <SelectFromLayoutLibraryDialog
              open={libraryModalOpen}
              onOpenChange={setLibraryModalOpen}
              onSelectLayout={handleSelectFromLibrary}
            />
          </Card>
        );
      }}
    />
  );
}
